import { supabase } from '../lib/supabase';

export interface BrandProfile {
  id: string;
  userId: string;
  companyName: string;
  industry: string;
  website?: string;
  description?: string;
  verified: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BrandMetrics {
  totalCampaigns: number;
  activeCampaigns: number;
  totalSpent: number;
  totalReach: number;
  averageROI: number;
  totalApplications: number;
}

export const brandService = {
  /**
   * Get brand profile by user ID
   */
  async getBrandProfileByUserId(userId: string): Promise<BrandProfile | null> {
    const { data, error } = await supabase
      .from('brand_profiles')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    
    if (!data) return null;

    return {
      id: data.id,
      userId: data.user_id,
      companyName: data.company_name,
      industry: data.industry,
      website: data.website,
      description: data.description,
      verified: data.verified,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  },

  /**
   * Get current authenticated brand profile
   */
  async getCurrentBrandProfile(): Promise<BrandProfile | null> {
    const { data: auth } = await supabase.auth.getUser();
    const user = auth?.user;
    if (!user) return null;
    return await brandService.getBrandProfileByUserId(user.id);
  },

  /**
   * Create or update brand profile
   */
  async upsertBrandProfile(payload: Partial<BrandProfile>): Promise<BrandProfile> {
    const { data: auth } = await supabase.auth.getUser();
    const user = auth?.user;
    if (!user) throw new Error('Not authenticated');

    // Check if profile already exists
    const { data: existing } = await supabase
      .from('brand_profiles')
      .select()
      .eq('user_id', user.id)
      .maybeSingle();

    const row = {
      user_id: user.id,
      company_name: payload.companyName,
      industry: payload.industry,
      website: payload.website,
      description: payload.description
    };

    let data;
    if (existing) {
      // Update existing profile
      const result = await supabase
        .from('brand_profiles')
        .update(row)
        .eq('user_id', user.id)
        .select()
        .single();
      if (result.error) throw result.error;
      data = result.data;
    } else {
      // Insert new profile
      const result = await supabase
        .from('brand_profiles')
        .insert(row)
        .select()
        .single();
      if (result.error) throw result.error;
      data = result.data;
    }

    return {
      id: data.id,
      userId: data.user_id,
      companyName: data.company_name,
      industry: data.industry,
      website: data.website,
      description: data.description,
      verified: data.verified,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  },

  /**
   * Get brand dashboard metrics using client-side aggregation
   */
  async getBrandMetrics(brandId: string): Promise<BrandMetrics> {
    // Use client-side aggregation (RPC function removed to avoid 400 errors)
    const { data: campaigns, error: campaignsError } = await supabase
      .from('campaigns')
      .select('id, budget, spent, current_reach, roi, status')
      .eq('brand_id', brandId)
      .order('created_at', { ascending: false });

    if (campaignsError) throw campaignsError;

    const totalCampaigns = campaigns?.length || 0;
    const activeCampaigns = campaigns?.filter(c => c.status === 'active').length || 0;
    const totalSpent = campaigns?.reduce((sum, c) => sum + (Number(c.spent) || 0), 0) || 0;
    const totalReach = campaigns?.reduce((sum, c) => sum + (Number(c.current_reach) || 0), 0) || 0;
    const averageROI = campaigns?.length 
      ? campaigns.reduce((sum, c) => sum + (Number(c.roi) || 0), 0) / campaigns.length 
      : 0;

    const campaignIds = campaigns?.map((c: { id: string }) => c.id) || [];
    let totalApplications = 0;

    if (campaignIds.length > 0) {
      const { count, error: appsError } = await supabase
        .from('campaign_applications')
        .select('id', { count: 'exact', head: true })
        .in('campaign_id', campaignIds);

      if (appsError) throw appsError;
      totalApplications = count || 0;
    }

    return {
      totalCampaigns,
      activeCampaigns,
      totalSpent,
      totalReach,
      averageROI,
      totalApplications
    };
  },

  /**
   * Get brand metrics with change percentages compared to previous period
   * Compares current 30 days vs previous 30 days
   */
  async getBrandMetricsWithChanges(brandId: string): Promise<{
    metrics: BrandMetrics;
    changes: {
      totalReachChange: number;
      totalSpentChange: number;
      activeCampaignsChange: number;
      averageROIChange: number;
      totalApplicationsChange: number;
    };
  }> {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

    try {
      // Fetch all campaigns for this brand
      const { data: allCampaigns, error: campaignsError } = await supabase
        .from('campaigns')
        .select('id, budget, spent, current_reach, roi, status, created_at, updated_at')
        .eq('brand_id', brandId);

      if (campaignsError) throw campaignsError;

      const campaigns = allCampaigns || [];
      
      // Current period metrics (last 30 days of activity)
      const currentPeriodCampaigns = campaigns.filter(c => {
        const updatedAt = new Date(c.updated_at || c.created_at);
        return updatedAt >= thirtyDaysAgo;
      });

      // Previous period metrics (30-60 days ago)
      const previousPeriodCampaigns = campaigns.filter(c => {
        const updatedAt = new Date(c.updated_at || c.created_at);
        return updatedAt >= sixtyDaysAgo && updatedAt < thirtyDaysAgo;
      });

      // Calculate current metrics
      const totalCampaigns = campaigns.length;
      const activeCampaigns = campaigns.filter(c => c.status === 'active' || c.status === 'open').length;
      const totalSpent = campaigns.reduce((sum, c) => sum + (Number(c.spent) || 0), 0);
      const totalReach = campaigns.reduce((sum, c) => sum + (Number(c.current_reach) || 0), 0);
      const averageROI = campaigns.length 
        ? campaigns.reduce((sum, c) => sum + (Number(c.roi) || 0), 0) / campaigns.length 
        : 0;

      // Calculate previous period metrics for comparison
      const prevActiveCampaigns = previousPeriodCampaigns.filter(c => c.status === 'active' || c.status === 'open').length;
      const prevTotalSpent = previousPeriodCampaigns.reduce((sum, c) => sum + (Number(c.spent) || 0), 0);
      const prevTotalReach = previousPeriodCampaigns.reduce((sum, c) => sum + (Number(c.current_reach) || 0), 0);
      const prevAverageROI = previousPeriodCampaigns.length 
        ? previousPeriodCampaigns.reduce((sum, c) => sum + (Number(c.roi) || 0), 0) / previousPeriodCampaigns.length 
        : 0;

      // Get applications count
      const campaignIds = campaigns.map(c => c.id);
      let totalApplications = 0;
      let prevApplications = 0;

      if (campaignIds.length > 0) {
        const { count } = await supabase
          .from('campaign_applications')
          .select('id', { count: 'exact', head: true })
          .in('campaign_id', campaignIds);
        totalApplications = count || 0;

        // Previous period applications
        const { count: prevCount } = await supabase
          .from('campaign_applications')
          .select('id', { count: 'exact', head: true })
          .in('campaign_id', campaignIds)
          .lt('created_at', thirtyDaysAgo.toISOString())
          .gte('created_at', sixtyDaysAgo.toISOString());
        prevApplications = prevCount || 0;
      }

      // Calculate percentage changes (avoid division by zero)
      const calcChange = (current: number, previous: number): number => {
        if (previous === 0) return current > 0 ? 100 : 0;
        return ((current - previous) / previous) * 100;
      };

      return {
        metrics: {
          totalCampaigns,
          activeCampaigns,
          totalSpent,
          totalReach,
          averageROI,
          totalApplications
        },
        changes: {
          totalReachChange: calcChange(totalReach, prevTotalReach),
          totalSpentChange: calcChange(totalSpent, prevTotalSpent),
          activeCampaignsChange: calcChange(activeCampaigns, prevActiveCampaigns),
          averageROIChange: calcChange(averageROI, prevAverageROI),
          totalApplicationsChange: calcChange(totalApplications, prevApplications)
        }
      };
    } catch (error) {
      console.error('Error getting metrics with changes:', error);
      // Return fallback with zero changes
      const basicMetrics = await this.getBrandMetrics(brandId);
      return {
        metrics: basicMetrics,
        changes: {
          totalReachChange: 0,
          totalSpentChange: 0,
          activeCampaignsChange: 0,
          averageROIChange: 0,
          totalApplicationsChange: 0
        }
      };
    }
  },

  /**
   * Aggregate application stats across all campaigns for a brand
   */
  async getApplicationStats(brandId: string): Promise<{ total: number; approved: number; rejected: number; pending: number; }>{
    // Fetch campaign IDs first
    const { data: campaigns, error: cErr } = await supabase
      .from('campaigns')
      .select('id')
      .eq('brand_id', brandId);
    if (cErr) throw cErr;
    const ids = campaigns?.map((c: { id: string }) => c.id) || [];
    if (ids.length === 0) return { total: 0, approved: 0, rejected: 0, pending: 0 };

    // Get counts by status
    const statuses = ['approved', 'rejected', 'pending'] as const;
    let counts: Record<string, number> = { approved: 0, rejected: 0, pending: 0 };

    for (const status of statuses) {
      const { count, error } = await supabase
        .from('campaign_applications')
        .select('id', { count: 'exact', head: true })
        .in('campaign_id', ids)
        .eq('status', status);
      if (error) throw error;
      counts[status] = count || 0;
    }

    const { count: totalCount, error: tErr } = await supabase
      .from('campaign_applications')
      .select('id', { count: 'exact', head: true })
      .in('campaign_id', ids);
    if (tErr) throw tErr;

    return {
      total: totalCount || 0,
      approved: counts.approved,
      rejected: counts.rejected,
      pending: counts.pending
    };
  },

  /**
   * Get a count of pending applications for all campaigns of a brand
   * Includes: pending applications AND submitted deliverables awaiting review
   */
  async getPendingApplicationsCount(brandId: string): Promise<number> {
    try {
      const { data: campaigns, error: cErr } = await supabase
        .from('campaigns')
        .select('id')
        .eq('brand_id', brandId);
      if (cErr) throw cErr;
      const ids = campaigns?.map((c: { id: string }) => c.id) || [];
      if (ids.length === 0) return 0;
      
      // Count pending applications (creators wanting to join)
      const { data: pendingApps, error: pendingError } = await supabase
        .from('campaign_applications')
        .select('id')
        .in('campaign_id', ids)
        .eq('status', 'pending');
      if (pendingError) {
        console.error('Error counting pending applications:', pendingError);
        return 0;
      }
      
      // Count completed (submitted work) awaiting review
      // Note: ENUM values are: pending, approved, rejected, completed
      let submittedCount = 0;
      try {
        const { data: submittedWork, error: submittedError } = await supabase
          .from('campaign_applications')
          .select('id')
          .in('campaign_id', ids)
          .eq('status', 'completed');
        if (!submittedError) {
          submittedCount = submittedWork?.length || 0;
        }
      } catch (e) {
        console.warn('Could not count submitted deliverables:', e);
      }
      
      return (pendingApps?.length || 0) + submittedCount;
    } catch (error) {
      console.error('Error in getPendingApplicationsCount:', error);
      return 0;
    }
  },

  /**
   * Get count of unread messages for a brand
   */
  async getUnreadMessagesCount(brandId: string): Promise<number> {
    try {
      // Count unread messages where the brand is a participant
      const { count, error } = await supabase
        .from('messages')
        .select('id', { count: 'exact', head: true })
        .eq('recipient_id', brandId)
        .eq('is_read', false);
      
      if (error) {
        console.error('Error counting unread messages:', error);
        return 0;
      }
      return count || 0;
    } catch (error) {
      console.error('Error in getUnreadMessagesCount:', error);
      return 0;
    }
  },

  /**
   * Get count of submitted deliverables awaiting brand review
   */
  async getSubmittedDeliverablesCount(brandId: string): Promise<number> {
    try {
      const { data: campaigns, error: cErr } = await supabase
        .from('campaigns')
        .select('id')
        .eq('brand_id', brandId);
      if (cErr) throw cErr;
      const ids = campaigns?.map((c: { id: string }) => c.id) || [];
      if (ids.length === 0) return 0;
      
      // Count applications with status='completed' (creator submitted work)
      // Note: ENUM values are: pending, approved, rejected, completed
      const { data, error } = await supabase
        .from('campaign_applications')
        .select('id')
        .in('campaign_id', ids)
        .eq('status', 'completed');
      
      if (error) {
        console.error('Error counting submitted deliverables:', error);
        return 0;
      }
      return data?.length || 0;
    } catch (error) {
      console.error('Error in getSubmittedDeliverablesCount:', error);
      return 0;
    }
  },

  /**
   * List brand campaigns with lightweight fields
   */
  async listBrandCampaigns(brandId: string, limit = 20) {
    const { data, error } = await supabase
      .from('campaigns')
      .select('id, title, status, budget, spent, current_reach, created_at')
      .eq('brand_id', brandId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return data || [];
  },

  /**
   * Get applications for brand's campaigns
   */
  async getBrandApplications(brandId: string, status?: string) {
    try {
      // First get all campaign IDs for this brand
      const { data: campaigns, error: cErr } = await supabase
        .from('campaigns')
        .select('id')
        .eq('brand_id', brandId);
      
      if (cErr) {
        console.error('Error fetching campaigns:', cErr);
        return [];
      }
      
      const campaignIds = campaigns?.map((c: { id: string }) => c.id) || [];
      if (campaignIds.length === 0) return [];
      
      // Use simpler query without FK relationships to avoid PGRST200 errors
      // Then fetch creator data separately
      let query = supabase
        .from('campaign_applications')
        .select(`
          *,
          campaign:campaigns(
            id,
            title,
            category,
            status
          )
        `)
        .in('campaign_id', campaignIds)
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;
      if (error) {
        console.error('Error fetching applications:', error);
        return [];
      }
      
      // Fetch creator profiles separately - only use user_id lookup to avoid 400 errors
      if (data && data.length > 0) {
        const creatorIds = [...new Set(data.map((app: any) => app.creator_id).filter(Boolean))];
        
        if (creatorIds.length > 0) {
          // Only query by user_id to avoid 400 errors from invalid ID lookups
          const { data: creatorsByUserId, error: creatorsError } = await supabase
            .from('creator_profiles')
            .select('id, user_id, username, category, followers, engagement_rate, avatar_url, full_name')
            .in('user_id', creatorIds);
          
          if (!creatorsError && creatorsByUserId) {
            // Create a map for quick lookup
            const creatorMap = new Map();
            creatorsByUserId.forEach((cp: any) => {
              creatorMap.set(cp.user_id, cp);
              creatorMap.set(cp.id, cp); // Also map by id
            });
          
            // Merge creator data into applications
            data.forEach((app: any) => {
              const creator = creatorMap.get(app.creator_id);
              if (creator) {
                app.creator = creator;
              }
            });
          
            // Fetch user profiles if needed
            const userIds = [...new Set(
              data.filter((app: any) => app.creator?.user_id)
                .map((app: any) => app.creator.user_id)
            )];
          
            if (userIds.length > 0) {
              const { data: userProfiles } = await supabase
                .from('user_profiles')
                .select('id, full_name, avatar_url')
                .in('id', userIds);
            
              const userMap = new Map((userProfiles || []).map((u: any) => [u.id, u]));
              data.forEach((app: any) => {
                if (app.creator?.user_id) {
                  const userProfile = userMap.get(app.creator.user_id);
                  if (userProfile) {
                    app.creator.user_profiles = userProfile;
                  }
                }
              });
            }
          }
        }
      }
      
      return data || [];
    } catch (error) {
      console.error('Error in getBrandApplications:', error);
      return [];
    }
  },

  /**
   * Approve or reject campaign application
   */
  async updateApplicationStatus(
    applicationId: string, 
    status: 'approved' | 'rejected',
    feedback?: string
  ) {
    const { data, error } = await supabase
      .from('campaign_applications')
      .update({ 
        status, 
        feedback,
        updated_at: new Date().toISOString()
      })
      .eq('id', applicationId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Get brand's campaign analytics
   */
  async getCampaignAnalytics(campaignId: string) {
    const { data: campaign, error: campaignError } = await supabase
      .from('campaigns')
      .select(`
        *,
        campaign_applications(
          id,
          status,
          creator_id,
          created_at
        )
      `)
      .eq('id', campaignId)
      .single();

    if (campaignError) throw campaignError;

    const applications = campaign.campaign_applications || [];
    const approved = applications.filter((a: any) => a.status === 'approved').length;
    const pending = applications.filter((a: any) => a.status === 'pending').length;
    const rejected = applications.filter((a: any) => a.status === 'rejected').length;

    return {
      campaign,
      analytics: {
        totalApplications: applications.length,
        approvedApplications: approved,
        pendingApplications: pending,
        rejectedApplications: rejected,
        budget: campaign.budget,
        spent: campaign.spent,
        remainingBudget: campaign.budget - campaign.spent,
        currentReach: campaign.current_reach,
        targetReach: campaign.target_reach,
        reachProgress: (campaign.current_reach / campaign.target_reach) * 100,
        roi: campaign.roi
      }
    };
  },

  /**
   * Invite creator to campaign
   */
  async inviteCreatorToCampaign(campaignId: string, creatorId: string, message?: string) {
    // Get current user (brand) id
    const { data: authData } = await supabase.auth.getUser();
    if (!authData?.user?.id) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase
      .from('campaign_invitations')
      .insert({
        campaign_id: campaignId,
        creator_id: creatorId,
        brand_id: authData.user.id,
        invitation_message: message,
        status: 'sent'
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating invitation:', error);
      throw error;
    }
    return data;
  },

  /**
   * Get brand's invited creators
   */
  async getBrandInvitations(brandId: string, status?: string) {
    try {
      console.log('📩 getBrandInvitations called with brandId:', brandId, 'status:', status);
      
      // First get all campaign IDs for this brand
      const { data: campaigns, error: cErr } = await supabase
        .from('campaigns')
        .select('id')
        .eq('brand_id', brandId);
      
      if (cErr) {
        console.error('Error fetching campaigns:', cErr);
        throw cErr;
      }
      
      console.log('📋 Found campaigns for brand:', campaigns?.length || 0);
      
      const campaignIds = campaigns?.map((c: { id: string }) => c.id) || [];
      if (campaignIds.length === 0) {
        console.log('⚠️ No campaigns found for brand, returning empty');
        return [];
      }

      // Try campaign_invitations table first (used by brandService.inviteCreatorToCampaign)
      let query = supabase
        .from('campaign_invitations')
        .select(`
          *,
          campaign:campaigns(
            id,
            title,
            category,
            brand_id
          ),
          creator:creator_profiles(
            id,
            username,
            category,
            followers,
            engagement_rate,
            user_id
          )
        `)
        .in('campaign_id', campaignIds)
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;
      
      if (error) {
        console.log('⚠️ campaign_invitations query failed, trying invitations table...', error.message);
        
        // Try the invitations table as fallback
        let fallbackQuery = supabase
          .from('invitations')
          .select(`
            *,
            campaign:campaigns(
              id,
              title,
              category,
              brand_id
            ),
            creator:creator_profiles(
              id,
              username,
              category,
              followers,
              engagement_rate,
              user_id
            )
          `)
          .in('campaign_id', campaignIds)
          .order('created_at', { ascending: false });

        if (status) {
          fallbackQuery = fallbackQuery.eq('status', status);
        }

        const { data: fallbackData, error: fallbackError } = await fallbackQuery;
        
        if (fallbackError) {
          console.error('❌ Both invitation tables failed:', fallbackError);
          return [];
        }
        
        console.log('✅ Found invitations from invitations table:', fallbackData?.length || 0);
        return await this.enrichWithUserProfiles(fallbackData || []);
      }
      
      console.log('✅ Found invitations from campaign_invitations table:', data?.length || 0);
      return await this.enrichWithUserProfiles(data || []);
    } catch (error) {
      console.error('Error in getBrandInvitations:', error);
      return [];
    }
  },
  
  // Helper to enrich invitation data with user profiles
  async enrichWithUserProfiles(data: any[]) {
    if (!data || data.length === 0) return [];
    
    const userIds = data
      .filter((inv: any) => inv.creator?.user_id)
      .map((inv: any) => inv.creator.user_id);
    
    if (userIds.length > 0) {
      const { data: userProfiles } = await supabase
        .from('user_profiles')
        .select('id, full_name, avatar_url')
        .in('id', userIds);
      
      const userMap = new Map((userProfiles || []).map((u: any) => [u.id, u]));
      data.forEach((inv: any) => {
        if (inv.creator?.user_id) {
          const userProfile = userMap.get(inv.creator.user_id);
          if (userProfile) {
            inv.creator.user_profiles = userProfile;
          }
        }
      });
    }
    
    return data;
  },

  /**
   * Get count of invitations for a brand by status (or all)
   */
  async getBrandInvitationsCount(brandId: string, status?: 'pending' | 'accepted' | 'rejected' | 'cancelled') {
    try {
      // First get all campaign IDs for this brand
      const { data: campaigns, error: cErr } = await supabase
        .from('campaigns')
        .select('id')
        .eq('brand_id', brandId);
      
      if (cErr) {
        console.error('Error fetching campaigns for invitations count:', cErr);
        return 0;
      }
      
      const campaignIds = campaigns?.map((c: { id: string }) => c.id) || [];
      if (campaignIds.length === 0) return 0;
      
      // Try campaign_invitations table first (used by brandService.inviteCreatorToCampaign)
      let query = supabase
        .from('campaign_invitations')
        .select('id', { count: 'exact', head: true })
        .in('campaign_id', campaignIds);

      if (status) {
        query = query.eq('status', status);
      }

      const { count, error } = await query;
      
      if (error) {
        // Fallback to invitations table
        console.log('Trying invitations table for count...');
        let fallbackQuery = supabase
          .from('invitations')
          .select('id', { count: 'exact', head: true })
          .in('campaign_id', campaignIds);

        if (status) {
          fallbackQuery = fallbackQuery.eq('status', status);
        }

        const { count: fallbackCount, error: fallbackError } = await fallbackQuery;
        if (fallbackError) {
          console.error('Error counting invitations:', fallbackError);
          return 0;
        }
        return fallbackCount || 0;
      }
      
      return count || 0;
    } catch (error) {
      console.error('Error in getBrandInvitationsCount:', error);
      return 0;
    }
  },

  /**
   * Search creators for collaboration
   */
  async searchCreators(filters: {
    category?: string;
    minFollowers?: number;
    maxFollowers?: number;
    minEngagement?: number;
    minRating?: number;
  }) {
    let query = supabase
      .from('creator_profiles')
      .select(`
        *,
        user_id
      `);

    if (filters.category) {
      query = query.eq('category', filters.category);
    }
    if (filters.minFollowers) {
      query = query.gte('followers', filters.minFollowers);
    }
    if (filters.maxFollowers) {
      query = query.lte('followers', filters.maxFollowers);
    }
    if (filters.minEngagement) {
      query = query.gte('engagement_rate', filters.minEngagement);
    }
    if (filters.minRating) {
      query = query.gte('rating', filters.minRating);
    }

    query = query.order('rating', { ascending: false }).limit(20);

    const { data, error } = await query;
    if (error) throw error;
    
    // Fetch user profiles separately
    if (data && data.length > 0) {
      const userIds = data.filter((c: any) => c.user_id).map((c: any) => c.user_id);
      if (userIds.length > 0) {
        const { data: userProfiles } = await supabase
          .from('user_profiles')
          .select('id, full_name, avatar_url')
          .in('id', userIds);
        
        const userMap = new Map((userProfiles || []).map((u: any) => [u.id, u]));
        data.forEach((creator: any) => {
          if (creator.user_id) {
            creator.user_profiles = userMap.get(creator.user_id);
          }
        });
      }
    }
    
    return data;
  },

  /**
   * Get real-time updates for brand dashboard
   */
  subscribeToBrandUpdates(brandId: string, callback: (payload: any) => void) {
    const channels = [
      supabase
        .channel(`campaigns_${brandId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'campaigns',
            filter: `brand_id=eq.${brandId}`,
          },
          callback
        )
        .subscribe(),

      supabase
        .channel(`applications_${brandId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'campaign_applications',
          },
          callback
        )
        .subscribe(),
    ];

    return () => {
      channels.forEach(channel => channel.unsubscribe());
    };
  }
};
